import { useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';

import type {
  GetClassSettingsQuery,
  GetClassSettingsQueryVariables,
} from './class-settings.graphql.generated';
import {
  GET_CLASS_SETTINGS,
  UPDATE_CLASS_SETTINGS,
} from './class-settings.graphql';
import type {
  UpdateClassSettingsMutation,
  UpdateClassSettingsMutationVariables,
} from './class-settings.graphql.generated';
import { useLoggingQuery, useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';

export const useClassSettings = (classId: number | null) => {
  const { showError } = useSnackbar();

  const { data, loading } = useLoggingQuery<
    GetClassSettingsQuery,
    GetClassSettingsQueryVariables
  >(GET_CLASS_SETTINGS, {
    skip: !classId,
    variables: {
      input: {
        classId: classId!,
      },
    },
  });
  const settings = data?.getClassSettings;

  const backgroundVisibility =
    !settings || settings.backgroundVisibility === null
      ? true
      : settings.backgroundVisibility;
  const animationAutoPlay =
    !settings || settings.animationAutoPlay === null
      ? true
      : settings.animationAutoPlay;
  const translationEnabled = !!settings?.translationEnabled;
  const translationEnabledStudent = !!settings?.translationEnabledStudent;
  const ttsEnabled = !!settings?.ttsEnabled;

  const [updateClassSettings, { client }] = useLoggingMutation<
    UpdateClassSettingsMutation,
    UpdateClassSettingsMutationVariables
  >(UPDATE_CLASS_SETTINGS, {
    onError: () =>
      showError('There was an error updating the settings. Please try again'),
  });

  const handleUpdate = useCallback(
    ({
      classId,
      backgroundVisibility,
      animationAutoPlay,
      translationEnabled,
      translationEnabledStudent,
    }: {
      classId: number;
      backgroundVisibility: boolean;
      animationAutoPlay: boolean;
      translationEnabled: boolean;
      translationEnabledStudent: boolean;
    }) => {
      client.writeQuery<GetClassSettingsQuery, GetClassSettingsQueryVariables>({
        query: GET_CLASS_SETTINGS,
        variables: {
          input: { classId },
        },
        data: {
          getClassSettings: {
            __typename: 'UserClassSettings',
            backgroundVisibility,
            animationAutoPlay,
            translationEnabled,
            translationEnabledStudent,
            ttsEnabled,
          },
        },
      });

      updateClassSettings({
        variables: {
          input: {
            classId,
            backgroundVisibility,
            animationAutoPlay,
            translationEnabled,
            translationEnabledStudent,
          },
        },
        optimisticResponse: {
          updateClassSettings: {
            __typename: 'UserClass',
            id: classId,
            settings: {
              __typename: 'UserClassSettings',
              animationAutoPlay,
              backgroundVisibility,
              translationEnabled,
              translationEnabledStudent,
              ttsEnabled,
            },
          },
        },
      });
    },
    [ttsEnabled, client, updateClassSettings]
  );

  const handleDebounceUpdate = useMemo(
    () => debounce(handleUpdate, 300),
    [handleUpdate]
  );

  return {
    data,
    loading,
    backgroundVisibility,
    animationAutoPlay,
    translationEnabled,
    translationEnabledStudent,
    ttsEnabled,
    handleUpdate: handleDebounceUpdate,
  };
};
